import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import times from 'lodash/times';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { GoalDTO, GoalType, useGoalsQuery } from './api/useGoalsQuery';
import { Shimmer } from '../../CommonComponents/OPL/Shimmer/Shimmer';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { WorkflowWrapper } from '../../Workflow/Workflow.styles';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { GoalTile } from './components/GoalTile/GoalTile';
import { Goal } from './components/Goal/Goal';
import { TilesGrid } from './Goals.styles';
import { BUY_CAR_GOAL_NAME, NEW_HOME_PURCHASE_GOAL, NEW_LARGE_PURCHASE_GOAL } from './Goals.meta';
import { useWorkflowError } from './hooks/useWorkflowError';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import PulseCheckLogo from '../../PulseCheck/img/tile-logo.svg';
import { hasAccessToEditProfileSelector } from '../../CommonSelectors/CommonAreaSelector';

interface WrapperProps {
  isWorkflow: boolean;
  children: React.ReactNode;
}

const Wrapper = ({ isWorkflow, children }: WrapperProps) => {
  if (isWorkflow) {
    return (
      <WorkflowWrapper>
        <Card p="32">{children}</Card>
      </WorkflowWrapper>
    );
  }

  return <>{children}</>;
};

interface Props {
  clientId: string;
  workflowProps: WorkflowComponentProps;
}

export const Goals = ({ clientId, workflowProps }: Props) => {
  useEffectOnce(() => {
    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.GoalSelectionStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  const { error: workflowError, onValidate, reset: resetWorkflowError } = useWorkflowError({
    clientId,
    isWorkflow: !!workflowProps,
  });

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Goals"
          description="Tap on the goals you’d like to add to your plan."
        />

        {workflowError && (
          <Alert
            message={workflowError}
            onRequestClose={resetWorkflowError}
            variant="error"
            mb="16"
          />
        )}

        <GoalsContent clientId={clientId} />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={() => {
            const error = onValidate();

            if (!error) {
              workflowProps.onNext();
            }
          }}
        />
      )}
    </>
  );
};

interface GoalsContentProps {
  clientId: string;
}

const GoalsContent = ({ clientId }: GoalsContentProps) => {
  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const { isLoading, isError, refetch, data } = useGoalsQuery({ clientId });

  const [goal, setGoal] = useState<GoalDTO | null>(null);

  useEffectOnce(() => {
    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.GoalSelectionStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  if (isLoading) {
    return (
      <TilesGrid as="section">
        {times(10).map(key => (
          <Shimmer key={key} />
        ))}
      </TilesGrid>
    );
  }

  if (isError) {
    return <ErrorState onAction={refetch} mx="auto" my="56" />;
  }

  if (!data) {
    return null;
  }

  const { goals, yearsOptions, mortgageTypeOptions } = data;

  const getCustomIcon = (g: GoalDTO) => {
    if (g.type === GoalType.LargePurchase && g.goalOrigin === 'PulseCheck') {
      return (
        <PulseCheckLogo
          width={24}
          css={`
            color: ${({ theme }) => theme.colors.primary['600']};
          `}
        />
      );
    }

    return undefined;
  };

  const getTileDisabledMessage = (g: GoalDTO) => {
    if (g.disabledMessage) {
      return g.disabledMessage;
    }

    if (!canEdit && !g.isSelected) {
      return 'You do not have permission to edit goals.';
    }

    return undefined;
  };

  return (
    <>
      <TilesGrid as="main">
        {goals.map(g => (
          <GoalTile
            key={g.name}
            type={g.type}
            name={g.name}
            disabledMessage={getTileDisabledMessage(g)}
            checked={g.type === GoalType.LargePurchase ? true : g.isSelected}
            icon={g.name === BUY_CAR_GOAL_NAME ? 'far fa-car-side' : undefined}
            onClick={() => {
              if (!canEdit && !g.isSelected) {
                return;
              }

              setGoal(g);
            }}
            customIcon={getCustomIcon(g)}
          />
        ))}

        {canEdit && !goals.find(({ name }) => name === BUY_CAR_GOAL_NAME) && (
          <GoalTile
            type={GoalType.LargePurchase}
            name={BUY_CAR_GOAL_NAME}
            icon="far fa-car-side"
            onClick={() => setGoal({ ...NEW_LARGE_PURCHASE_GOAL, name: BUY_CAR_GOAL_NAME })}
          />
        )}

        {canEdit && (
          <GoalTile
            type={GoalType.LargePurchase}
            name="Another large purchase..."
            onClick={() => setGoal(NEW_LARGE_PURCHASE_GOAL)}
          />
        )}

        {canEdit && goals.find(({ type, id }) => !!id && type === GoalType.HomePurchase) && (
          <GoalTile
            type={GoalType.HomePurchase}
            name="Another home purchase..."
            onClick={() => setGoal(NEW_HOME_PURCHASE_GOAL)}
          />
        )}
      </TilesGrid>

      {goal && (
        <Goal
          {...goal}
          clientId={clientId}
          onClose={() => setGoal(null)}
          yearsOptions={yearsOptions}
          mortgageTypeOptions={mortgageTypeOptions}
        />
      )}
    </>
  );
};
